import { useArcConnections } from "./ArcConnectionsContext";
import { CollapsibleToolGroup } from "../reusable/CollapsibleToolGroup";
import { DebouncedInput } from "../reusable/DebouncedInput";
import { PresetSelector } from "../reusable/PresetSelector";
import { ToolDescriptionPopover } from "../reusable/ToolDescriptionPopover";
import { Link2, Palette, Paintbrush, Share2 } from "lucide-react";
import { SendSvgToToolMenu } from "../reusable/SendSvgToToolMenu";
import { UndoRedoControls } from "../reusable/UndoRedoControls";

export function ArcConnectionsControls() {
    const {
        bezierAngle,
        bezierAngleRandomness,
        connectionDistanceThreshold,
        connectionChance,
        angleFlipPercent,
        centerAngleThreshold,
        brushSize,
        brushDensity,
        nodeBaseSize,
        nodeSizeIncrement,
        nodeSizeRandomness,
        nodeColor,
        strokeColor,
        connectionOpacity,
        nodes,
        setBezierAngle,
        setBezierAngleRandomness,
        setConnectionDistanceThreshold,
        setConnectionChance,
        setAngleFlipPercent,
        setCenterAngleThreshold,
        setBrushSize,
        setBrushDensity,
        setNodeBaseSize,
        setNodeSizeIncrement,
        setNodeSizeRandomness,
        setNodeColor,
        setStrokeColor,
        setConnectionOpacity,
        clearNodes,
        buildExportSvgString,
        handleDownload,
        applyPreset,
        getPresetValues,
        resetParameters,
        undo,
        redo,
        canUndo,
        canRedo,
    } = useArcConnections();

    const handleBezierAngleChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue)) return;
        setBezierAngle(numValue);
    };

    const handleBezierAngleRandomnessChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue)) return;
        const clamped = Math.max(0, Math.min(1, numValue));
        setBezierAngleRandomness(clamped);
    };

    const handleConnectionDistanceChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue <= 0) return;
        setConnectionDistanceThreshold(numValue);
    };

    const handleConnectionChanceChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue)) return;
        const clamped = Math.max(0, Math.min(100, numValue));
        setConnectionChance(clamped);
    };

    const handleAngleFlipPercentChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue)) return;
        const clamped = Math.max(0, Math.min(100, numValue));
        setAngleFlipPercent(clamped);
    };

    const handleCenterAngleThresholdChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue)) return;
        const clamped = Math.max(0, Math.min(180, numValue));
        setCenterAngleThreshold(clamped);
    };

    const handleBrushSizeChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 0) return;
        setBrushSize(numValue);
    };

    const handleBrushDensityChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 0) return;
        setBrushDensity(numValue);
    };

    const handleNodeBaseSizeChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue <= 0) return;
        setNodeBaseSize(numValue);
    };

    const handleNodeSizeIncrementChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue) || numValue < 0) return;
        setNodeSizeIncrement(numValue);
    };

    const handleNodeSizeRandomnessChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue)) return;
        const clamped = Math.max(0, Math.min(1, numValue));
        setNodeSizeRandomness(clamped);
    };

    const handleConnectionOpacityChange = (v: string | number) => {
        const numValue = typeof v === "number" ? v : Number(v);
        if (!Number.isFinite(numValue)) return;
        const clamped = Math.max(0, Math.min(1, numValue));
        setConnectionOpacity(clamped);
    };

    return (
        <div className="panel-controls arc-connections-controls">
            <div className="tool-header">
                <div className="tool-header-title-row">
                    <h2>Arc Connections</h2>
                    <ToolDescriptionPopover dialogLabel="Arc Connections tool description">
                        <p>
                            Click on the canvas to add nodes. Nodes within the
                            connection distance threshold are linked with
                            bezier curves.
                        </p>
                    </ToolDescriptionPopover>
                </div>
                <div className="tool-header-actions">
                    <UndoRedoControls
                        canUndo={canUndo}
                        canRedo={canRedo}
                        onUndo={undo}
                        onRedo={redo}
                    />
                    <button
                        type="button"
                        className="generic-button"
                        onClick={resetParameters}
                    >
                        Reset parameters
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={handleDownload}
                        disabled={nodes.length === 0}
                    >
                        Download SVG
                    </button>
                    <button
                        type="button"
                        className="generic-button"
                        onClick={clearNodes}
                        disabled={nodes.length === 0}
                    >
                        Clear all nodes
                    </button>
                </div>
            </div>
            <div className="path-tool-inputs">
                <PresetSelector
                    toolId="arc-connections"
                    onApply={applyPreset}
                    onExport={getPresetValues}
                />
                <p className="control-panel-hint">
                    {nodes.length === 0
                        ? "Click the canvas to place nodes."
                        : `${nodes.length} node${nodes.length === 1 ? "" : "s"} placed.`}
                </p>
                <CollapsibleToolGroup title="Connections & curves" icon={Link2}>
                    <div className="field-row">
                        <label className="field-label">
                            Connection distance threshold (px)
                            <DebouncedInput
                                type="number"
                                min={1}
                                step={10}
                                value={connectionDistanceThreshold}
                                onChange={handleConnectionDistanceChange}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Connection chance (%)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={100}
                                step={5}
                                value={connectionChance}
                                onChange={handleConnectionChanceChange}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Bezier angle (degrees)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={90}
                                step={5}
                                value={bezierAngle}
                                onChange={handleBezierAngleChange}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Bezier angle randomness (0-1)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.1}
                                value={bezierAngleRandomness}
                                onChange={handleBezierAngleRandomnessChange}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Angle flip percent (%)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={100}
                                step={5}
                                value={angleFlipPercent}
                                onChange={handleAngleFlipPercentChange}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Center angle threshold (degrees)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={180}
                                step={5}
                                value={centerAngleThreshold}
                                onChange={handleCenterAngleThresholdChange}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Brush & nodes" icon={Paintbrush}>
                    <div className="field-row">
                        <label className="field-label">
                            Brush size (px)
                            <DebouncedInput
                                type="number"
                                min={0}
                                step={5}
                                value={brushSize}
                                onChange={handleBrushSizeChange}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Brush density (nodes per click)
                            <DebouncedInput
                                type="number"
                                min={0}
                                step={0.1}
                                value={brushDensity}
                                onChange={handleBrushDensityChange}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Node base size (px)
                            <DebouncedInput
                                type="number"
                                min={1}
                                step={1}
                                value={nodeBaseSize}
                                onChange={handleNodeBaseSizeChange}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Node size increment per connection (px)
                            <DebouncedInput
                                type="number"
                                min={0}
                                step={1}
                                value={nodeSizeIncrement}
                                onChange={handleNodeSizeIncrementChange}
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Node size randomness (0-1)
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.1}
                                value={nodeSizeRandomness}
                                onChange={handleNodeSizeRandomnessChange}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>

                <CollapsibleToolGroup title="Appearance" icon={Palette}>
                    <div className="field-row">
                        <label className="field-label">
                            Node color
                            <input
                                type="color"
                                value={nodeColor}
                                onChange={(e) => setNodeColor(e.target.value)}
                                className="control-color-block"
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Stroke color
                            <input
                                type="color"
                                value={strokeColor}
                                onChange={(e) => setStrokeColor(e.target.value)}
                                className="control-color-block"
                            />
                        </label>
                    </div>

                    <div className="field-row">
                        <label className="field-label">
                            Connection opacity
                            <DebouncedInput
                                type="number"
                                min={0}
                                max={1}
                                step={0.1}
                                value={connectionOpacity}
                                onChange={handleConnectionOpacityChange}
                            />
                        </label>
                    </div>
                </CollapsibleToolGroup>
                <CollapsibleToolGroup title="Send to tool" icon={Share2}>
                    <SendSvgToToolMenu
                        disabled={nodes.length === 0}
                        getSvg={() => ({
                            name: "arc-connections-export.svg",
                            content: buildExportSvgString(),
                        })}
                        allowedSlots={["path", "object", "primary"]}
                        excludeTargetPanels={["arc-connections"]}
                    />
                </CollapsibleToolGroup>
            </div>
        </div>
    );
}
